import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, Loader2 } from 'lucide-react';
import { StackStatus, getStackStatus } from '@/services/perfTestsApi';

interface Props {
  onReadyChange?: (ready: boolean) => void;
}

export function StackStatusPanel({ onReadyChange }: Props) {
  const [status, setStatus] = useState<StackStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async () => {
    setRefreshing(true);
    try {
      const s = await getStackStatus();
      setStatus(s);
      onReadyChange?.(s.ready);
    } catch {
      setStatus(null);
      onReadyChange?.(false);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Vérification de la stack…
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
        <XCircle className="w-4 h-4" />
        Impossible de joindre l'endpoint stack/status
      </div>
    );
  }

  const services = [
    { name: 'PostgreSQL', s: status.postgres, hint: 'kaizen_perf_postgres' },
    { name: 'Redis', s: status.redis, hint: 'kaizen_perf_redis' },
    { name: 'Mock LLM', s: status.mockLlm, hint: 'kaizen_perf_mock_llm' },
    { name: 'Backend cible', s: status.targetBackend, hint: status.targetBackend.url },
  ];

  return (
    <div
      className={`border rounded-lg p-3 ${
        status.ready ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-800">État de la stack</h3>
        <button
          onClick={fetchStatus}
          disabled={refreshing}
          className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
          Vérifier
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {services.map((svc) => (
          <div key={svc.name} className="flex items-start gap-2 text-xs">
            {svc.s.running ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="min-w-0">
              <div className="font-medium text-gray-900">{svc.name}</div>
              <div className="text-gray-500 truncate" title={svc.hint}>
                {svc.s.detail ?? (svc.s.running ? 'up' : 'down')}
              </div>
            </div>
          </div>
        ))}
      </div>
      {!status.ready && (
        <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-900">
          Démarre la stack avec <code className="bg-amber-100 px-1 rounded">npx nx run easyobeya_backend:perf-stack-up</code>
        </div>
      )}
    </div>
  );
}
